import { DateTime, Interval } from 'luxon';
import { Injectable, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from 'src/app/services/app.service';
import { ChatMessageModel } from 'src/app/models/chat';
import { environment } from 'src/environments/environment';
import { MessagesService } from './messages.service';
import { SocketService } from '../../../services/socket/socket.service';
import { ShiftsFacade } from './facades/shifts/shifts.facade';
import { NotificationsFacade } from './facades/notifications/notifications.facade';
import { ShiftsAccessor } from './accessors/shifts.accessor';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import { FacilitiesAccessor } from './accessors/facilities.accessor';
import { TimecardsFacade } from './facades/timecards/timecards.facade';
import { ModalService } from './ui/modal.service';
import { AuthFacade } from 'src/app/services/facades/auth/auth.facade';
import { ChatsFacade } from './facades/chats/chats.facade';
import { FacilitiesFacade } from './facades/facilities/facilities.facade';
import { BrazeService, EVENT_OPTIONS } from './braze.service';
import { getFormattedDate, getFormattedTime } from '../helpers/getFormattedDateTime';
import { ModalBuilderService} from './modal-builder.service';
import { NurseEtaService } from './nurse-eta-service';
import { UserFacade } from 'src/app/services/facades/user/user.facade';

type NewMessage = { facility: string; message: ChatMessageModel };

@Injectable()
export class SseService {
  constructor(
    app: AppService,
    private socket: SocketService,
    private messages: MessagesService,
    private router: Router,
    private shiftsFacade: ShiftsFacade,
    private notificationsFacade: NotificationsFacade,
    private shiftsAccessor: ShiftsAccessor,
    private facilitiesAccessor: FacilitiesAccessor,
    private timecardsFacade: TimecardsFacade,
    private modalService: ModalService,
    private authFacade: AuthFacade,
    private chatsFacade: ChatsFacade,
    private facilitiesFacade: FacilitiesFacade,
    private modalBuilderService: ModalBuilderService,
    private nurseEtaService: NurseEtaService,
    private userFacade: UserFacade
  ) {
    app.expose('facades', 'sse', this);
  }

  register() {
    const {chat, shifts, timecards, user, facility, ncns, notification, geolocation} = environment.sse;
    this.socket.on(chat.CHAT_NEW_MESSAGE, (payload: NewMessage) => {
      console.log(chat.CHAT_NEW_MESSAGE, payload);
      this.notificationsFacade.increaseChatsUnreadMessagesCount({ inc: 1 });
      this.chatsFacade.fetchChat(payload.message.thread);
      this.chatsFacade.fetchMessages(payload.message.thread);
      // this.store.dispatch(sendMessageSuccess({ message: payload.message }));
      // this.messages.add('New chat message', 'action', () => {
      //   this.router.navigate(['manager/chat'], {
      //     state: { threadId: payload.message.thread },
      //   });
      // });
    });
    this.socket.on(shifts.SHIFTS_INVITE_REVOKED, (payload)=>{
      this.shiftsFacade.fetchShift(payload.shifts.id);
    });
    this.socket.on(shifts.SHIFTS_INVITE_EXPIRED, (payload) => {
      this.facilitiesAccessor.activeFacility$
      .pipe(filter((f) => !!f),take(1))
      .subscribe((f) => {
        if (f) {
          this.shiftsFacade.fetchShift(payload.shifts.id);
        }
      });
    });
    this.socket.on(shifts.SHIFT_APPLICANT_REFUSED_INVITE, (payload)=>{
      this.facilitiesAccessor.activeFacility$
      .pipe(filter((f) => !!f),take(1))
      .subscribe((f) => {
        if (f) {
          let date = getFormattedDate(payload.shifts.shiftData.shiftFrom,f.timezone,);
          let time = getFormattedTime(payload.shifts.shiftData.shiftFrom, payload.shifts.shiftData.shiftTo, f.timezone);
          if (payload.shiftStatus == 'close') {
            this.shiftsFacade.removeShiftFromStore(payload.shifts.id);
            this.messages.add(`${payload.applicant.fullName}, ${payload.shifts.license}` +
              ` refused your invite to a shift on ${date} at ${time}. The shift has been removed.`, 'Shift invite refused.', 'info');
          }
          else{
            this.shiftsFacade.fetchShift(payload.shifts.id);
            this.messages.add(`${payload.applicant.fullName}, ${payload.shifts.license}` +
              ` refused your invite to a shift on ${date} at ${time}. The shift is now open to other nurses.`, 'Shift invite refused.',
              'info', () => {
                this.router.navigate(['/manager/facility-selection']).then(() => {
                  this.router.navigate(['manager/shifts/applicants'], {
                    state: { id: payload.shifts._id },
                  });
                });
              }, true);
          }
        }
      });
    });
    this.socket.on(shifts.SHIFT_APPLICANT_ACCEPTED_INVITE, (payload)=>{
      this.shiftsFacade.fetchShift(payload.shifts.id);
    });
    this.socket.on(shifts.N_SHIFT_APPLICANT_ACCEPTED_INVITE, (payload)=>{
      this.shiftsFacade.fetchShift(payload.shifts.id);
      this.facilitiesAccessor.activeFacility$
      .pipe(filter((f) => !!f),take(1))
      .subscribe((f) => {
        if (f) {
            let date = getFormattedDate(payload.shifts.shiftData.shiftFrom,f.timezone,);
            let time = getFormattedTime(payload.shifts.shiftData.shiftFrom, payload.shifts.shiftData.shiftTo, f.timezone);
            this.messages.add(`${payload.applicant.fullName}, ${payload.shifts.license} is confirmed to work on ${date} at ${time}.`,
              'Your shift invite has been accepted.',
              'info',
              () => {
                this.router.navigate(['/manager/facility-selection']).then(() => {
                  this.router.navigate(['manager/shifts/applicants'], {
                    state: { id: payload.shifts._id },
                  });
                });
              }, true);
        }
      });
    });
    this.socket.on(shifts.SHIFTS_3D_SINCE_APPLICATION_MANAGER, (payload) => {
      console.log(shifts.SHIFTS_3D_SINCE_APPLICATION_MANAGER, payload);


      this.shiftsFacade.addReminderIntoQueue(
        this.generateReminderObj('SHIFTS_3D_SINCE_APPLICATION_MANAGER', payload)
      );
    });
    this.socket.on(shifts.SHIFTS_7D_SINCE_APPLICATION_MANAGER, (payload) => {
      console.log(shifts.SHIFTS_7D_SINCE_APPLICATION_MANAGER, payload);
      this.shiftsFacade.addReminderIntoQueue(
        this.generateReminderObj('SHIFTS_7D_SINCE_APPLICATION_MANAGER', payload)
      );
    });
    this.socket.on(shifts.SHIFTS_14D_SINCE_APPLICATION_MANAGER, (payload) => {
      console.log(shifts.SHIFTS_14D_SINCE_APPLICATION_MANAGER, payload);
      this.shiftsFacade.addReminderIntoQueue(
        this.generateReminderObj('SHIFTS_14D_SINCE_APPLICATION_MANAGER', payload)
      );
    });

    this.socket.on(shifts.SHIFTS_NEW_APPLICANT, (payload) => {
      console.log(shifts.SHIFTS_NEW_APPLICANT, payload);
      this.shiftsFacade.fetchShift(payload.shifts._id);
      this.notificationsFacade.increaseShiftsUpdatedCount(
        payload.shifts._id,
        'applied',
      );
      this.facilitiesAccessor.activeFacility$
        .pipe(
          filter((f) => !!f),
          take(1),
        )
        .subscribe((f) => {
          if (f) {
            let date = getFormattedDate(
              payload.shifts.shiftData.shiftFrom,
              f.timezone,
            ); //example - October 13th 2021
            let time =
              DateTime.fromISO(payload.shifts.shiftData.shiftFrom, {
                zone: f.timezone,
              }).toFormat('h:mma') +
              ' - ' +
              DateTime.fromISO(payload.shifts.shiftData.shiftTo, {
                zone: f.timezone,
              }).toFormat('h:mma');
            this.messages.add(
              'Applicant ' +
                payload.applicant.fullName +
                ' has applied to your shift on ' +
                date +
                ' at ' +
                time +
                '.',
              '',
              'info',
              () => {
                this.router.navigate(['/manager/facility-selection']).then(() => {
                  this.router.navigate(['manager/shifts/applicants'], {
                    state: { id: payload.shifts._id },
                  });
                });
              },
              true,
            );
          }
        });
    });
    this.socket.on(shifts.SHIFT_APPLICANT_CALLED_OUT, payload => {
      console.log(shifts.SHIFT_APPLICANT_CALLED_OUT, payload);
      this.shiftsFacade.fetchShift(payload.shifts._id);
      this.notificationsFacade.increaseShiftsUpdatedCount(payload.shifts._id, 'calledOut');
      this.facilitiesAccessor.activeFacility$
        .pipe(
          filter(f => !!f),
          take(1),
          withLatestFrom(this.userFacade.state.user$)
        )
        .subscribe(([f, user]) => {
          if (f) {
            let date = getFormattedDate(payload.shifts.shiftData.shiftFrom, f.timezone); //example - October 13th 2021

            let time = DateTime.fromISO(payload.shifts.shiftData.shiftFrom, { zone: f.timezone  }).toFormat('h:mma') +
               ' - ' + DateTime.fromISO(payload.shifts.shiftData.shiftTo, { zone: f.timezone}).toFormat('h:mma');
               
            const shiftStart = DateTime.fromISO(payload.shifts.shiftData.shiftFrom, { zone: f.timezone });
            const hoursUntilShift = shiftStart.diff(DateTime.now().setZone(f.timezone), 'hours').hours;
            const cadence = user?.fmCommunicationSettings?.calloutNotificationsCadence || '12h';
            let showNotification = this.showNotificationBasedOnCadence(cadence, hoursUntilShift);

            if (showNotification) {
              this.messages.add(
                'Uh-oh, applicant called out of their shift on ' + date + ' at ' + time + '.',
                '',
                'warning',
                () => {
                  this.router.navigate(['/manager/facility-selection']).then(() => {
                    this.router.navigate(['manager/shifts/applicants'], {
                      state: { id: payload.shifts._id }
                    });
                  });
                },
                true
              );
          }
        }
      });
    });
    this.socket.on(shifts.SHIFT_APPLICANT_WITHDREW, (payload) => {
      console.log(shifts.SHIFT_APPLICANT_WITHDREW, payload);
      //this.notificationsFacade.increaseShiftsUpdatedCount({id: payload.shifts._id});
      this.shiftsFacade.fetchShift(payload.shifts._id);
      this.facilitiesAccessor.activeFacility$
        .pipe(
          filter((f) => !!f),
          take(1),
        )
        .subscribe((f) => {
          if (f) {
            let date = getFormattedDate(
              payload.shifts.shiftData.shiftFrom,
              f.timezone,
            ); //example - October 13th 2021

            let time =
              DateTime.fromISO(payload.shifts.shiftData.shiftFrom, {
                zone: f.timezone,
              }).toFormat('h:mma') +
              ' - ' +
              DateTime.fromISO(payload.shifts.shiftData.shiftTo, {
                zone: f.timezone,
              }).toFormat('h:mma');

            this.messages.add(
              'Applicant ' +
                payload.applicant.fullName +
                ' withdrew their application from your shift on ' +
                date +
                ' at ' +
                time +
                '.',
              '',
              'warning',
              () => {
                this.router.navigate(['/manager/facility-selection']).then(() => {
                  this.router.navigate(['manager/shifts/applicants'], {
                    state: { id: payload.shifts._id },
                  });
                });
              },
              true,
            );
          }
        });
      console.log('payload,', payload);
    });
    this.socket.on(shifts.SHIFTS_INSTASHYFT_TAKEN, (payload) => {
      console.log(shifts.SHIFTS_INSTASHYFT_TAKEN, payload);
      this.shiftsFacade.fetchShift(payload.shifts);
    });
    this.socket.on(shifts.SHIFTS_APPLICANT_CONFIRMED, (payload)=>{
      this.shiftsFacade.fetchShift(payload.shifts);
    })
    this.socket.on(timecards.TIMECARD_UPDATED, (payload) => {
      console.log(timecards.TIMECARD_UPDATED, payload);
      this.timecardsFacade.fetchTimecard(payload.timecard._id);
      this.shiftsFacade.fetchShift(payload.timecard.shift._id);
    });
    this.socket.on(timecards.TIMECARD_CREATED, (payload) => {
      console.log(timecards.TIMECARD_CREATED, payload),
      this.shiftsFacade.fetchShift(payload.timecard.shift);
    });
    this.socket.on(ncns.NCNS_REPORTED_BY_FM, (payload) => {
      console.log(ncns.NCNS_REPORTED_BY_FM, payload),
      this.shiftsFacade.fetchShift(payload.shiftId);
    });
    this.socket.on(ncns.NCNS_REPORTED_BY_FM_UNDONE, (payload) => {
      console.log(ncns.NCNS_REPORTED_BY_FM_UNDONE, payload),
      this.shiftsFacade.fetchShift(payload.shiftId);
    });
    this.socket.on(shifts.SHIFTS_APPLICANT_DISABLED, (payload) => {
      console.log(shifts.SHIFTS_APPLICANT_DISABLED, payload);
      payload.shifts.forEach((s) => {
        this.shiftsFacade.updateApplicantStatusToSuspended(
          payload.applicant._id,
          s._id,
        );
        const date = getFormattedDate(
          s.shiftData.shiftFrom,
          s.facility.timezone,
        ); //example - October 13th 2021

        const time =
          DateTime.fromISO(s.shiftData.shiftFrom, {
            zone: s.facility.timezone,
          }).toFormat('h:mma') +
          ' - ' +
          DateTime.fromISO(s.shiftData.shiftTo, {
            zone: s.facility.timezone,
          }).toFormat('h:mma');
        if (s.status === 'confirmed') {
          this.messages.add(
            payload.applicant.name.first +
              payload.applicant.name.last +
              ', ' +
              s.license +
              ' has been suspended. They’ve been cancelled for their shift at ' +
              s.facility.name +
              ' on  ' +
              date +
              ' at ' +
              time,

            '',
            'warning',
            () => {
              this.router.navigate(['/manager/facility-selection']).then(() => {
                  this.router.navigate(['manager/shifts/applicants'], {
                    state: { id: s._id },
                  });
                });
            },
            true,
          );
        }
      });
    });
    this.socket.on(shifts.SHIFTS_OVERTIME_CHANGED, (payload) => {
      this.facilitiesAccessor.activeFacility$
        .pipe(
          filter((f) => !!f),
          take(1),
        )
        .subscribe((f) => {
          if (f._id === payload.facility) {
            this.shiftsAccessor.shifts$.pipe(take(1)).subscribe((shifts) => {
              let shiftsIds = [];
              payload.shifts.forEach((s) => {
                if (shifts[s._id]) {
                  shiftsIds.push(s._id);
                }
              });
              this.fetchShiftsOneByOne(shiftsIds, 1);
            });
          }
        });
      console.log(shifts.SHIFTS_OVERTIME_CHANGED, payload);
    });
    this.socket.on(user.SERVICE_USER_DISABLED, (payload) => {
      this.modalService
        .openModal({
          modalTitle: 'Your account has been suspended',
          modalBody: 'Your access to ESHYFT has been suspended. Please contact us at support@eshyft.com to restore it.',
          okButtonText: 'Okay',
          cancelButtonText: null,
          color: 'cna',
          type: 'submit'
        })
        .pipe(take(1))
        .pipe(take(1))
        .subscribe((r) => {
          this.authFacade.logout().pipe(take(1)).subscribe();
        });
    });
    this.socket.on(facility.FACILITY_DEACTIVATION_STATUS_UPDATED, (payload) => {
      console.log('FACILITY_DEACTIVATION_STATUS_UPDATED',payload);
      if(payload.facilityModel.deactivationStatus === 'Deactivated'){
        this.authFacade.logout().pipe(take(1)).subscribe();
        this.messages.add('The facility has been deactivated. If you have any questions please, please contact support.', '', 'danger', ()=>{
          window.location.href = 'mailto:support@eshyft.com';
        }, true, 'Contact support');
      }else{
        this.facilitiesFacade.fetchFacilityById(payload.facility).pipe(take(1)).subscribe(r=>{
          let url = this.router.url;
          this.router.navigate(['/manager/facility-selection']).then(() => {
            this.router.navigate([url]);
          });
        })
      }
    });
    this.socket.on(notification.SCHEDULE_BLOCK, (payload) => {
      this.modalBuilderService.showBSPopup(payload);
    });
    this.socket.on(notification.TRUSTED_NURSE, (payload) => {
      this.modalBuilderService.showTrustedNursePopup(payload);
    });
    this.socket.on(geolocation.NURSE_GEOLOCATION_UPDATED, (payload) => {
      console.log(geolocation.NURSE_GEOLOCATION_UPDATED, payload);
      this.nurseEtaService.updateApplicantEta(
        payload.shiftId,
        {
          nurseId: payload.nurseId,
          eta: payload.eta,
          pingTime: payload.pingTime,
          isWithinFacilityClockInDistance: payload.isWithinFacilityClockInDistance
        },
      );
    });
    this.socket.on(geolocation.SHIFT_APPLICANT_ETA_UPDATED, (payload) => {
      this.shiftsFacade.fetchShift(payload.shiftId);
    });
    console.log('SSE Registered!');
  }
  showNotificationBasedOnCadence(cadence: string, hoursUntilShift: number): boolean {
    switch (cadence) {
      case 'immediate':
        return true;
      case '12h':
        return hoursUntilShift <= 12;
      case '36h':
        return hoursUntilShift <= 36;
      default:
        return false;
    }
  }

  fetchShiftsOneByOne(shiftsIds, i) {
    if (shiftsIds.length < i) {
      return;
    }
    this.shiftsFacade
      .fetchShift(shiftsIds[i - 1])
      .pipe(take(1))
      .subscribe((shiftData) => {
        this.fetchShiftsOneByOne(shiftsIds, i + 1);
      });
  }

  generateReminderObj(event, payload){
    const timeText = {
      'SHIFTS_3D_SINCE_APPLICATION_MANAGER': '72 hours',
      'SHIFTS_7D_SINCE_APPLICATION_MANAGER': '7 days',
      'SHIFTS_14D_SINCE_APPLICATION_MANAGER': '14 days',
    }
    let date = DateTime.fromISO(payload.shifts.shiftData.shiftFrom, {
      zone: payload.shifts.facility.timezone,
    }).toFormat('LL/dd/yyyy')
    let time =
      DateTime.fromISO(payload.shifts.shiftData.shiftFrom, {
        zone: payload.shifts.facility.timezone,
      }).toFormat('h:mma') +
      ' - ' +
      DateTime.fromISO(payload.shifts.shiftData.shiftTo, {
        zone: payload.shifts.facility.timezone,
      }).toFormat('h:mma');
    const reminderObj = {
      facility: payload.facility,
      applicant: payload.applicant,
      shift: payload.shifts,
      text: 'It’s been ' + timeText[event] + ' since ' + payload.applicant.user.name.first + ' applied to your ' + payload.shifts.license + ' shift on ' +
      date + ' at ' + time +
      '. Please confirm or decline this applicant.',
    };
    return reminderObj
  }
}
